import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAlbumContext } from '@/contexts/AlbumProvider';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentAlbumSlug, getNetlifyFunctionsRoot } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader2 } from 'lucide-react';

interface ScreenPasswordGateProps {
  children: React.ReactNode;
  backgroundStyle?: React.CSSProperties;
  scope?: string; // differentiate storage key per page
}

const ScreenPasswordGate: React.FC<ScreenPasswordGateProps> = ({ children, backgroundStyle, scope = 'default' }) => {
  const { albumId, albumSlug } = useAlbumContext();
  const { albumSlug: albumSlugParam } = useParams<{ albumSlug?: string }>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { t } = useTranslation();
  const codeParam = searchParams.get('code');
  const storageKey = useMemo(
    () => (albumId ? `bk:${albumId}:${scope}:screenAuthorized` : null),
    [albumId, scope],
  );
  const autoValidatedCodeRef = useRef<string | null>(null);

  const [authorizedKey, setAuthorizedKey] = useState<string | null>(null);
  const [keyInput, setKeyInput] = useState(codeParam ?? '');
  const [checking, setChecking] = useState(false);
  const [autoValidating, setAutoValidating] = useState(Boolean(codeParam));

  const sessionAuthorized = useMemo(() => {
    if (!storageKey || typeof window === 'undefined') return false;

    try {
      return sessionStorage.getItem(storageKey) === 'true';
    } catch {
      return false;
    }
  }, [storageKey]);

  const isAuthorized = sessionAuthorized || (storageKey !== null && authorizedKey === storageKey);
  const isLoading = !isAuthorized && (checking || autoValidating);

  useEffect(() => {
    if (!storageKey) {
      setAuthorizedKey(null);
      return;
    }

    setAuthorizedKey((currentKey) => (currentKey === storageKey ? currentKey : null));
  }, [storageKey]);

  const validatePassword = useCallback(async (password: string, options?: { isAutoValidation?: boolean }) => {
    if (!password) return;

    setChecking(true);
    if (options?.isAutoValidation) {
      setAutoValidating(true);
    }

    try {
      const slug = albumSlugParam || albumSlug || getCurrentAlbumSlug() || '';
      const res = await fetch(`${getNetlifyFunctionsRoot()}/pw-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ albumSlug: slug, pwValue: password }),
      });

      if (res.ok) {
        const expose = res.headers.get('Access-Control-Expose-Headers') || '';
        const hasExposed = expose.toLowerCase().includes('x-album-screenkey-valid');
        const hasHeader = !!res.headers.get('x-album-screenkey-valid');
        if (hasExposed || hasHeader) {
          if (storageKey) {
            setAuthorizedKey(storageKey);
          }
          try {
            if (storageKey) sessionStorage.setItem(storageKey, 'true');
          } catch {
            // Ignore storage write failures and continue with in-memory authorization.
          }
          return;
        }
      }

      toast({ title: t('error'), description: t('accessDenied'), variant: 'destructive' });
    } catch (e) {
      Sentry.captureException(e, { extra: { context: 'pw-check submit' } });
      toast({ title: t('error'), description: t('accessDenied'), variant: 'destructive' });
    } finally {
      setChecking(false);
      if (options?.isAutoValidation) {
        setAutoValidating(false);
      }
    }
  }, [albumSlug, albumSlugParam, storageKey, t, toast]);

  useEffect(() => {
    setKeyInput(codeParam ?? '');
    setAutoValidating(Boolean(codeParam));
    autoValidatedCodeRef.current = null;
  }, [storageKey, codeParam]);

  useEffect(() => {
    if (!codeParam || isAuthorized) {
      setAutoValidating(false);
      return;
    }

    setKeyInput(codeParam);

    if (checking || autoValidatedCodeRef.current === codeParam) {
      return;
    }

    autoValidatedCodeRef.current = codeParam;
    void validatePassword(codeParam, { isAutoValidation: true });
  }, [checking, codeParam, isAuthorized, validatePassword]);

  const handleSubmit = async () => {
    if (checking || !keyInput) return;
    validatePassword(keyInput);
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4" style={backgroundStyle}>
        {isLoading ? (
          <div
            className="flex items-center justify-center"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Loader2 className="h-16 w-16 animate-spin text-primary" />
            <span className="sr-only">{t('validating')}</span>
          </div>
        ) : (
          <div className="w-80 h-80 bg-card rounded-xl shadow-md border flex flex-col items-center justify-center p-6">
            <h1 className="text-lg font-semibold mb-4 text-center">{t('enterKeyTitle')}</h1>
            <Input
              type="text"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
              className="mb-3 text-center md:text-xl"
              autoFocus
            />
            <Button onClick={handleSubmit} disabled={checking || !keyInput} className="w-full">{t('access')}</Button>
          </div>
        )}
      </div>
    );
  }

  return <>{children}</>;
};

export default ScreenPasswordGate;
